'use client';

import { useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import { useApp } from '@/lib/AppContext';
import { useAuth } from '@/lib/AuthContext';
import { useProAccess } from '@/lib/hooks/useProAccess';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import {
  getTodayRecoveryMetrics,
  calculateReadinessScore,
  generateRecoveryInsights
} from '@/lib/recoveryCalculator';
import { getSmartRecommendationsSummary } from '@/lib/adaptive';
import { loadUserDataFromFirebase } from '@/lib/dataLoader';
import { calculateRecoveryDebt, type RecoveryDebtScore } from '@/lib/recoveryDebt';
import { calculateWeeklyVolume, type VolumeMetrics } from '@/lib/volumeTracking';
import { getSessionHistory } from '@/lib/sessionStorage';
import { createIntelligenceEngine, type IntelligenceReport } from '@/lib/unifiedIntelligence';
import { getTopArchetypeNudge, type ArchetypeNudge } from '@/lib/archetypeSystem';
import { loadProgressionState } from '@/lib/progressionService';
import { getActiveOperation, type OperationDefinition } from '@/lib/operations';
import { estimateSessionCalories, calculateDailyCalories } from '@/lib/calorieEstimator';
import { getPeriodizationPhase } from '@/lib/periodization';
import { OPERATOR_PERIODIZATION } from '@/lib/operatorPeriodization';
import type { ProgramMetadata } from '@/lib/types/program';
import type { RecoveryMetrics } from '@/lib/types/recovery';
import type { UserProgramSelection } from '@/lib/types/program';
import type { TrainingBreak } from '@/lib/types/trainingBreak';
import type { DailyActivity, ActivityTrend, NEATSuppressionResult, DailyCalorieSummary } from '@/lib/types/activity';
import { storage } from '@/lib/storage';
import { reentryStateFromHistory, type ReentryState } from '@/lib/reentryState';

// Types for active session detection
export interface ActiveSessionInfo {
  type: 'program' | 'freestyle';
  exerciseCount: number;
  lastSaved: Date;
  duration?: number;
}

export interface DashboardData {
  // Auth & context
  user: ReturnType<typeof useAuth>['user'];
  router: ReturnType<typeof useRouter>;
  currentWeek: number;
  currentDay: number;
  setCurrentWeek: (week: number) => void;
  setCurrentDay: (day: number) => void;
  oneRMs: Record<string, number>;
  isPro: boolean;
  isBeta: boolean;
  isLoadingProAccess: boolean;
  trialInfo: ReturnType<typeof useProAccess>['trialInfo'];
  hasPreviewAccess: boolean;
  previewWorkoutsRemaining: number | null;
  lapsedTrialInfo: ReturnType<typeof useProAccess>['lapsedTrialInfo'];

  // State
  todayRecovery: RecoveryMetrics | null;
  smartRecs: Awaited<ReturnType<typeof getSmartRecommendationsSummary>> | null;
  showToast: boolean;
  setShowToast: (v: boolean) => void;
  checkingOnboarding: boolean;
  showDayPicker: boolean;
  setShowDayPicker: (v: boolean) => void;
  programSelection: UserProgramSelection | null;
  setProgramSelection: (v: UserProgramSelection | null) => void;
  showProgramPrompt: boolean;
  setShowProgramPrompt: (v: boolean) => void;
  loadingProgramData: boolean;
  needsDeload: boolean;
  deloadReason: string;
  dismissedDeload: boolean;
  setDismissedDeload: (v: boolean) => void;
  recoveryDebt: RecoveryDebtScore | null;
  updatingDay: boolean;
  setUpdatingDay: (v: boolean) => void;
  showProgramPaywall: boolean;
  setShowProgramPaywall: (v: boolean) => void;
  volumeMetrics: VolumeMetrics | null;
  intelligenceReport: IntelligenceReport | null;
  workoutContext: import('@/lib/workoutContext').WorkoutContext | null;
  simpleMode: boolean;
  archetypeNudge: ArchetypeNudge | null;
  activeOp: OperationDefinition | null;
  activeSession: ActiveSessionInfo | null;
  todayActivity: DailyActivity | null;
  setTodayActivity: (v: DailyActivity | null) => void;
  activityTrend: ActivityTrend | null;
  neatSuppression: NEATSuppressionResult | null;
  dailyCalories: DailyCalorieSummary | null;
  gfitTokenExpired: boolean;
  setGfitTokenExpired: (v: boolean) => void;
  workoutTab: 'today' | 'tomorrow';
  setWorkoutTab: (v: 'today' | 'tomorrow') => void;
  userPath: 'program' | 'freestyle' | null;
  freestyleTemplates: import('@/lib/db').FreestyleTemplate[];
  recentFreestyleSessions: any[];
  activeBreak: TrainingBreak | null;
  setActiveBreak: (v: TrainingBreak | null) => void;
  showRecoverySkipConfirm: boolean;
  setShowRecoverySkipConfirm: (v: boolean) => void;
  pendingSurvey: string | null;
  surveyMisses: number;

  // Computed
  currentPhase: ReturnType<typeof getPeriodizationPhase>;
  reentryState: ReentryState | null;
  loading: boolean;

  // Actions
  handleApplyRecommendations: () => void;
  applyDeloadToToday: () => void;
  startDeloadedWorkout: () => void;
  startTodayWorkout: (skipRecoveryCheck?: boolean, overrideDeload?: boolean) => void;
  shouldShowAdvancedMetrics: () => boolean;
  shouldShowAIBrief: () => boolean;
  programSupportsRangeDay: (programId?: string | null) => boolean;
  handleRefreshGfit: () => Promise<void>;
  handleEndBreak: () => Promise<void>;
  handleDayClick: (day: number) => Promise<void>;
  handleRefreshProgramState: () => Promise<void>;
}

export function useDashboardData(): DashboardData {
  const router = useRouter();
  const { user } = useAuth();
  const { currentWeek, currentDay, setCurrentWeek, setCurrentDay, oneRMs } = useApp();
  const { isPro, isBeta, isLoading: isLoadingProAccess, trialInfo, hasPreviewAccess, previewWorkoutsRemaining, lapsedTrialInfo } = useProAccess();

  // All state variables
  const [todayRecovery, setTodayRecovery] = useState<RecoveryMetrics | null>(null);
  const [smartRecs, setSmartRecs] = useState<Awaited<ReturnType<typeof getSmartRecommendationsSummary>> | null>(null);
  const [showToast, setShowToast] = useState(false);
  const [checkingOnboarding, setCheckingOnboarding] = useState(true);
  const [showDayPicker, setShowDayPicker] = useState(false);
  const [programSelection, setProgramSelection] = useState<UserProgramSelection | null>(null);
  const [showProgramPrompt, setShowProgramPrompt] = useState(false);
  const [loadingProgramData, setLoadingProgramData] = useState(true);
  const [needsDeload, setNeedsDeload] = useState(false);
  const [deloadReason, setDeloadReason] = useState<string>('');
  const [dismissedDeload, setDismissedDeload] = useState(false);
  const [recoveryDebt, setRecoveryDebt] = useState<RecoveryDebtScore | null>(null);
  const [updatingDay, setUpdatingDay] = useState(false);
  const [showProgramPaywall, setShowProgramPaywall] = useState(false);
  const [volumeMetrics, setVolumeMetrics] = useState<VolumeMetrics | null>(null);
  const [intelligenceReport, setIntelligenceReport] = useState<IntelligenceReport | null>(null);
  const [workoutContext, setWorkoutContext] = useState<import('@/lib/workoutContext').WorkoutContext | null>(null);
  const [simpleMode, setSimpleMode] = useState<boolean>(false);
  const [archetypeNudge, setArchetypeNudge] = useState<ArchetypeNudge | null>(null);
  const [activeOp] = useState<OperationDefinition | null>(() => getActiveOperation());
  const [reentryState, setReentryState] = useState<ReentryState | null>(null);
  const [activeSession, setActiveSession] = useState<ActiveSessionInfo | null>(null);
  const [todayActivity, setTodayActivity] = useState<DailyActivity | null>(null);
  const [activityTrend, setActivityTrend] = useState<ActivityTrend | null>(null);
  const [neatSuppression, setNeatSuppression] = useState<NEATSuppressionResult | null>(null);
  const [dailyCalories, setDailyCalories] = useState<DailyCalorieSummary | null>(null);
  const [gfitTokenExpired, setGfitTokenExpired] = useState(false);
  const [workoutTab, setWorkoutTab] = useState<'today' | 'tomorrow'>('today');
  const [userPath, setUserPath] = useState<'program' | 'freestyle' | null>(null);
  const [freestyleTemplates, setFreestyleTemplates] = useState<import('@/lib/db').FreestyleTemplate[]>([]);
  const [recentFreestyleSessions, setRecentFreestyleSessions] = useState<any[]>([]);
  const [activeBreak, setActiveBreak] = useState<TrainingBreak | null>(null);
  const [showRecoverySkipConfirm, setShowRecoverySkipConfirm] = useState(false);
  const [pendingSurvey, setPendingSurvey] = useState<string | null>(null);
  const [surveyMisses, setSurveyMisses] = useState(0);
  const [experienceLevel, setExperienceLevel] = useState<'beginner' | 'intermediate' | 'advanced' | 'elite'>('intermediate');

  // === EFFECTS ===

  // Check for active session backups in localStorage + Dexie fallback.
  // Re-runs on mount, on visibility change, and on a custom
  // 'sessionDiscarded' event so a discard from anywhere in the app
  // immediately clears the dashboard "Workout in Progress" card
  // without waiting for the user to backstage the tab.
  useEffect(() => {
    const checkActiveSession = async () => {
      // V2 lives at activeSessionBackupV2 (V2 is the default session
      // display). Check it before falling through to V1's key.
      const v2Backup = storage.get('activeSessionBackupV2');
      if (v2Backup) {
        try {
          const data = JSON.parse(v2Backup);
          const lastSaved = new Date(data.lastSaved);
          const hoursSince = (Date.now() - lastSaved.getTime()) / (1000 * 60 * 60);
          if (hoursSince < 24 && data.exercises && data.exercises.length > 0) {
            const exercisesWithProgress = data.exercises.filter((e: any) =>
              e.sets && e.sets.some((s: any) => s.completed || s.weight > 0 || s.reps > 0)
            );
            if (exercisesWithProgress.length > 0) {
              setActiveSession({
                type: 'program',
                exerciseCount: exercisesWithProgress.length,
                lastSaved,
              });
              return;
            }
          }
        } catch (e) {
          console.error('Failed to parse V2 backup:', e);
        }
      }

      const programBackup = storage.get('activeSessionBackup');
      if (programBackup) {
        try {
          const data = JSON.parse(programBackup);
          const lastSaved = new Date(data.lastSaved);
          const hoursSince = (Date.now() - lastSaved.getTime()) / (1000 * 60 * 60);
          if (hoursSince < 24 && data.exercises && data.exercises.length > 0) {
            const exercisesWithProgress = data.exercises.filter((e: any) =>
              e.completed ||
              (e.sets && e.sets.some((s: any) => s.completed || s.weight > 0 || s.reps > 0)) ||
              (e.supersetRounds && e.supersetRounds.some((r: any) => r.exercises?.some((ex: any) => ex.completed || ex.weight > 0 || ex.reps > 0))) ||
              (e.circuitSets && e.circuitSets.some((s: any) => s.completed))
            );
            if (exercisesWithProgress.length > 0) {
              setActiveSession({
                type: 'program',
                exerciseCount: exercisesWithProgress.length,
                lastSaved,
              });
              return;
            }
          }
        } catch (e) {
          console.error('Failed to parse program backup:', e);
        }
      }

      const freestyleBackup = storage.get('freestyleSessionBackup');
      if (freestyleBackup) {
        try {
          const data = JSON.parse(freestyleBackup);
          const lastSaved = new Date(data.lastSaved);
          const hoursSince = (Date.now() - lastSaved.getTime()) / (1000 * 60 * 60);
          if (hoursSince < 24 && data.exercises && data.exercises.length > 0) {
            setActiveSession({
              type: 'freestyle',
              exerciseCount: data.exercises.length,
              lastSaved,
              duration: data.sessionTime,
            });
            return;
          }
        } catch (e) {
          console.error('Failed to parse freestyle backup:', e);
        }
      }

      // Fallback: check Dexie if localStorage had nothing (iOS Safari can evict localStorage)
      if (user?.uid) {
        try {
          const { loadActiveWorkout } = await import('@/lib/db');
          const dexieBackup = await loadActiveWorkout(user.uid);
          if (dexieBackup && dexieBackup.exercises?.length > 0) {
            const hoursSince = (Date.now() - new Date(dexieBackup.lastSaved).getTime()) / (1000 * 60 * 60);
            if (hoursSince < 24) {
              setActiveSession({
                type: dexieBackup.type === 'freestyle' ? 'freestyle' : 'program',
                exerciseCount: dexieBackup.exercises.length,
                lastSaved: new Date(dexieBackup.lastSaved),
              });
              return;
            }
          }
        } catch { /* non-critical */ }
      }

      setActiveSession(null);
    };

    checkActiveSession();

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkActiveSession();
      }
    };
    const handleDiscarded = () => {
      // Fast-path: a discard handler already cleared storage, so just
      // null out the local state immediately. Then re-run a real check
      // in case something else is still in flight.
      setActiveSession(null);
      checkActiveSession();
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('sessionDiscarded', handleDiscarded);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('sessionDiscarded', handleDiscarded);
    };
  }, []);

  // Check onboarding status
  // Use user.uid as dep (not the full user object) to avoid re-running on token refreshes
  const uid = user?.uid;
  useEffect(() => {
    // Safety net (App Store 2.1(a) "loads indefinitely after login"): if a
    // Firestore read stalls in iOS WKWebView, never leave the dashboard spinning
    // forever — force past the onboarding gate after 8s so the app renders with
    // cached Dexie data while background sync catches up.
    const onboardingSafety = setTimeout(() => setCheckingOnboarding(false), 8000);
    const checkOnboarding = async () => {
      if (!uid) {
        setCheckingOnboarding(false);
        return;
      }

      let isAdmin = false;
      try {
        const { isUserAdmin } = await import('@/lib/adminCheck');
        isAdmin = await isUserAdmin(uid);
      } catch {
        // Non-critical
      }

      // Retry logic: Firestore doc may not exist yet if AuthContext is still
      // writing it in the background (race with onAuthStateChanged).
      let userData: Record<string, any> = {};
      let docExists = false;
      for (let attempt = 0; attempt < 3; attempt++) {
        try {
          const userRef = doc(db, 'users', uid);
          const userDoc = await getDoc(userRef);
          docExists = userDoc.exists();
          userData = userDoc.data() || {};
          if (docExists) break;
        } catch (error) {
          console.error('Error reading user doc:', error);
        }
        // Doc doesn't exist yet — wait briefly for AuthContext to create it
        if (attempt < 2) {
          await new Promise(r => setTimeout(r, 800));
        }
      }

      const hasCompletedOnboarding = userData.onboardingComplete;

      // Admins skip onboarding check but still read user fields
      if (!isAdmin && (!docExists || !hasCompletedOnboarding)) {
        console.log('User needs onboarding, redirecting...');
        router.push('/onboarding-v2');
        return;
      }

      if (userData.showProgramPrompt === true && !userData.hasSelectedProgram) {
        setShowProgramPrompt(true);
      }

      setSimpleMode(userData.simpleMode === true);

      if (userData.userPath) {
        setUserPath(userData.userPath as 'program' | 'freestyle');
      }

      // Survey state intentionally unused in production — see beta cleanup
      // commit. The fields stay readable here for admin tooling but aren't
      // surfaced to the user.

      if (userData.experienceLevel) {
        setExperienceLevel(userData.experienceLevel);
      }

      setCheckingOnboarding(false);
    };

    checkOnboarding();
    return () => clearTimeout(onboardingSafety);
  }, [uid, router]);

  // Sync from Firebase + load all dashboard data (parallelized)
  useEffect(() => {
    if (checkingOnboarding) return;

    // Safety net (App Store 2.1(a)): guarantee the program-data spinner clears
    // even if Phase-1 Firestore sync stalls in WKWebView. Renders the dashboard
    // from cached Dexie data; the in-flight sync still resolves in the background.
    const loadSafety = setTimeout(() => setLoadingProgramData(false), 10000);

    const loadAllDashboardData = async () => {
      // ── Phase 1: Sync data & resolve program state (must complete first) ──
      if (user?.uid) {
        console.log('Dashboard: Syncing data from Firebase...');
        try {
          await loadUserDataFromFirebase(user.uid);

          const { getUserProgramState } = await import('@/lib/dayTracking');
          const state = await getUserProgramState(user.uid);

          if (state) {
            setProgramSelection(state.selection);
            setCurrentWeek(state.currentWeek);
            setCurrentDay(state.currentDay);
          } else {
            setProgramSelection(null);
            setShowProgramPrompt(true);
          }
        } catch (error) {
          console.error('Error syncing from Firebase:', error);
        } finally {
          setLoadingProgramData(false);
        }
      } else {
        setLoadingProgramData(false);
      }

      // ── Phase 2: Everything else in parallel ──
      const { getCurrentUserId } = await import('@/lib/anonymousAuth');
      const userId = getCurrentUserId();

      await Promise.allSettled([
        // Recovery + smart recs
        (async () => {
          const [recovery, recs] = await Promise.all([
            getTodayRecoveryMetrics(),
            getSmartRecommendationsSummary(),
          ]);
          setTodayRecovery(recovery);
          setSmartRecs(recs);
        })(),

        // Intelligence engine
        (async () => {
          try {
            const intelligence = await createIntelligenceEngine();
            const report = await intelligence.analyze();
            setIntelligenceReport(report);
          } catch (error) {
            console.error('Error loading intelligence:', error);
          }
        })(),

        // Activity data (Google Fit + NEAT)
        (async () => {
          if (!userId) return;
          try {
            try {
              // Sync gate: kick off a health-data refresh if the user
              // has connected ANY wearable source — googlefit (legacy
              // catch-all + Google Fit REST), apple-health (HealthKit),
              // or health-connect (Android HC). Previously this only
              // checked 'googlefit', which meant users who connected
              // via HealthKit/HC AFTER the union was widened (but
              // before the connect path started dual-writing the
              // legacy marker) silently stopped syncing. Symptom: step
              // count stuck on whatever value Dexie last cached.
              const { isWearableConnected } = await import('@/lib/wearableConnectionService');
              const [gfit, apple, hc] = await Promise.all([
                isWearableConnected(userId, 'googlefit'),
                isWearableConnected(userId, 'apple-health'),
                isWearableConnected(userId, 'health-connect'),
              ]);
              const wearableConnected = gfit || apple || hc;
              if (wearableConnected) {
                // Route through healthData so iOS picks HealthKit and
                // Android picks Health Connect when available. Falls
                // back to Google Fit REST on web.
                const { syncHealthData, detectHealthSource } = await import('@/lib/healthData');
                const synced = await syncHealthData(userId);
                if (!synced) {
                  const source = await detectHealthSource();
                  // Only the Google Fit web/fallback paths can land in
                  // the "token expired" state — HealthKit/HC use system
                  // permissions which don't expire on a timer.
                  if (source === 'google-fit-web' || source === 'google-fit-fallback') {
                    const { isTokenValid } = await import('@/lib/googleFitClient');
                    setGfitTokenExpired(!isTokenValid());
                  }
                }
              }
            } catch { /* non-critical */ }

            const { getTodayActivity, getDailyActivityEntries } = await import('@/lib/db');
            const today = await getTodayActivity(userId);
            if (today) {
              setTodayActivity(today as unknown as DailyActivity);
              const thirtyDaysAgo = new Date();
              thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
              const entries = await getDailyActivityEntries(userId, { since: thirtyDaysAgo });
              if (entries.length >= 7) {
                const { analyzeActivityTrend, detectNEATSuppression } = await import('@/lib/neatCalculator');
                setActivityTrend(analyzeActivityTrend(entries as unknown as DailyActivity[]));
                if (entries.length >= 14) {
                  setNeatSuppression(detectNEATSuppression(entries as unknown as DailyActivity[]));
                }
              }
            }
          } catch { /* no activity data — expected without wearable */ }
        })(),

        // Archetype nudge
        (async () => {
          if (!userId) return;
          try {
            const progressionState = await loadProgressionState(userId);
            const nudge = getTopArchetypeNudge(progressionState);
            setArchetypeNudge(nudge);
          } catch { /* non-critical */ }
        })(),

        // Training break
        (async () => {
          if (!user?.uid) return;
          try {
            const { getActiveBreak, hydrateBreakFromCloud } = await import('@/lib/trainingBreak');
            // Pull the break down first if this device has never seen it.
            // The break used to live in Dexie only, so a second device — or
            // this one after a cache clear — showed the normal program and
            // kept auto-advancing through a trip. No-op when a local break
            // already exists, so the local copy stays authoritative.
            await hydrateBreakFromCloud(user.uid);
            const breakObj = await getActiveBreak(user.uid);
            setActiveBreak(breakObj);
          } catch { /* non-critical */ }
        })(),
      ]);
    };

    loadAllDashboardData();

    // Track last refresh time to debounce visibility/focus events (5-min minimum)
    let lastRefreshTime = Date.now();
    const REFRESH_INTERVAL = 5 * 60 * 1000; // 5 minutes

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        const elapsed = Date.now() - lastRefreshTime;
        if (elapsed < REFRESH_INTERVAL) return;
        lastRefreshTime = Date.now();
        loadAllDashboardData();
      }
    };

    const handleFocus = () => {
      const elapsed = Date.now() - lastRefreshTime;
      if (elapsed < REFRESH_INTERVAL) return;
      lastRefreshTime = Date.now();
      loadAllDashboardData();
    };

    const handleWorkoutComplete = () => {
      import('../sessionStorage').then(m => m.invalidateSessionCache());
      lastRefreshTime = Date.now();
      loadAllDashboardData();
    };

    // Recovery / session sync — bypass the 5-min debounce. When the user
    // logs recovery on /recovery and navigates back, they expect today's
    // score to show up immediately, not in 5 minutes.
    const handleDataSynced = () => {
      lastRefreshTime = Date.now();
      loadAllDashboardData();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);
    window.addEventListener('workoutCompleted', handleWorkoutComplete);
    window.addEventListener('opmode-recovery-synced', handleDataSynced);
    window.addEventListener('opmode-session-synced', handleDataSynced);

    return () => {
      clearTimeout(loadSafety);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('workoutCompleted', handleWorkoutComplete);
      window.removeEventListener('opmode-recovery-synced', handleDataSynced);
      window.removeEventListener('opmode-session-synced', handleDataSynced);
    };
  }, [setCurrentWeek, setCurrentDay, user, checkingOnboarding]);

  // (Recovery data is loaded in the main dashboard effect above — no duplicate needed)

  // Load freestyle data
  useEffect(() => {
    if (userPath !== 'freestyle' || !user?.uid) return;
    let isMounted = true;

    const loadFreestyleData = async () => {
      try {
        const { getFreestyleTemplates, getSessions } = await import('@/lib/db');
        const [templates, sessions] = await Promise.all([
          getFreestyleTemplates(user.uid),
          getSessions(user.uid, { programSelection: 'free-lift' }),
        ]);
        if (!isMounted) return;
        setFreestyleTemplates(templates);
        // Keep ~2 weeks so the week tracker's muscle-balance + totals see
        // the whole current week, not just the last 5 sessions. The Recent
        // Sessions list slices this down to 5 for display.
        setRecentFreestyleSessions(sessions.slice(-14).reverse());
      } catch (error) {
        console.error('Error loading freestyle data:', error);
      }
    };

    loadFreestyleData();

    let lastFreestyleRefresh = Date.now();
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        const elapsed = Date.now() - lastFreestyleRefresh;
        if (elapsed < 5 * 60 * 1000) return; // 5-min debounce
        lastFreestyleRefresh = Date.now();
        loadFreestyleData();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      isMounted = false;
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [userPath, user?.uid]);

  // Reconcile missed-session reminder. Runs every time program /
  // week / day changes, so the scheduled push reflects the most
  // recent program state. The reconciler self-cancels on rest days
  // and when a session has already been logged today.
  useEffect(() => {
    if (!user?.uid) return;
    if (!programSelection?.programId) return;
    // Never nag during a training break. Travel mode tells the user their
    // program is paused, then the nightly "Mission Incomplete" push scolded
    // them for missing the very sessions it claimed to have paused — and
    // deep-linked to /session, the wrong screen for someone on a trip.
    if (activeBreak) {
      (async () => {
        try {
          const { cancelMissedSessionReminder } = await import('@/lib/missedSessionReminder');
          await cancelMissedSessionReminder();
        } catch { /* non-critical */ }
      })();
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { generateUserWorkout } = await import('@/lib/workoutGenerator');
        const { getCachedProgram } = await import('@/lib/programCache');
        const program = await getCachedProgram(programSelection.programId, user.uid);
        if (!program || cancelled) return;
        const exercises = await generateUserWorkout(program, currentWeek, currentDay, oneRMs || {});
        const isTrainingDay = Array.isArray(exercises) && exercises.some((ex: any) => {
          const block = String(ex?.block || '').toLowerCase();
          return !(block.includes('prep') || block.includes('recovery') ||
                   block.includes('warm') || block.includes('cool') || block.includes('activation'));
        });
        if (cancelled) return;
        const { reconcileMissedSessionReminder } = await import('@/lib/missedSessionReminder');
        await reconcileMissedSessionReminder(user.uid, isTrainingDay);
      } catch (e) {
        console.warn('[missed-session] dashboard reconcile skipped:', e);
      }
    })();
    return () => { cancelled = true; };
  }, [user?.uid, programSelection?.programId, currentWeek, currentDay, oneRMs, activeBreak]);

  // Detect workout context
  useEffect(() => {
    const detectContext = async () => {
      if (!programSelection) {
        setWorkoutContext(null);
        return;
      }

      try {
        const { detectWorkoutType, getWorkoutContext } = await import('@/lib/workoutContext');

        const sessions = await getSessionHistory();
        const todayStr = new Date().toDateString();
        const todaySession = sessions.find(s => new Date(s.date).toDateString() === todayStr);

        if (todaySession && 'exercises' in todaySession && todaySession.exercises && todaySession.exercises.length > 0) {
          const mockWorkout = {
            exercises: todaySession.exercises.map((ex: any) => ({
              name: ex.exerciseName || ex.name,
              exerciseType: ex.trackingType || 'weight-reps'
            }))
          };
          const type = detectWorkoutType(mockWorkout);
          const context = getWorkoutContext(mockWorkout, type);
          setWorkoutContext(context);
        } else {
          const mockWorkout = {
            exercises: [{ name: 'Strength Training', exerciseType: 'weight-reps' }]
          };
          const type = detectWorkoutType(mockWorkout);
          const context = getWorkoutContext(mockWorkout, type);
          setWorkoutContext(context);
        }
      } catch (error) {
        console.error('Error detecting workout context:', error);
      }
    };

    detectContext();
  }, [programSelection]);

  // Check for deload need
  useEffect(() => {
    let isMounted = true;
    let isRunning = false;

    const checkDeloadNeed = async () => {
      if (isRunning || !isMounted) return;
      isRunning = true;

      try {
        // experienceLevel is cached from the onboarding check — no extra Firestore read
        const debt = await calculateRecoveryDebt(28, experienceLevel);
        if (isMounted) setRecoveryDebt(debt);

        try {
          const allSessions = await getSessionHistory();
          const sessions = allSessions.filter(session => {
            if (!session.programSelection) return false;
            return session.programSelection === programSelection?.programId;
          });
          const metrics = calculateWeeklyVolume(sessions);
          if (isMounted) setVolumeMetrics(metrics);
        } catch (error) {
          console.error('Error calculating volume metrics:', error);
        }

        if (isMounted) {
          if (debt.needsDeload) {
            setNeedsDeload(true);
            setDeloadReason(debt.deloadReason || 'High recovery debt detected');
          } else {
            setNeedsDeload(false);
          }
        }
      } catch (error) {
        console.error('Error checking deload need:', error);
      } finally {
        isRunning = false;
      }
    };

    checkDeloadNeed();

    const interval = setInterval(checkDeloadNeed, 30 * 60 * 1000); // 30 min
    return () => {
      isMounted = false;
      clearInterval(interval);
    };
  }, [user?.uid, programSelection?.programId, experienceLevel]);

  // Daily calorie computation
  useEffect(() => {
    if (!user?.uid) return;
    let isMounted = true;

    async function computeCalories() {
      try {
        const allSessions = await getSessionHistory();
        const todayStr = new Date().toDateString();
        const todaySessions = allSessions.filter(
          s => new Date(s.date).toDateString() === todayStr
        );

        if (todaySessions.length === 0 && !todayActivity) {
          if (isMounted) setDailyCalories(null);
          return;
        }
        // Prefer the latest smart-scale reading — keeps daily calorie
        // burn in sync with the user's actual current weight without a
        // settings round-trip.
        const { getBodyWeightLbs } = await import('@/lib/weightResolver');
        const bodyWeightLbs = await getBodyWeightLbs(user?.uid);
        const summaries = todaySessions.map(s => estimateSessionCalories(s, bodyWeightLbs));
        const neatCals = todayActivity?.activeCalories || 0;
        const daily = calculateDailyCalories(summaries, neatCals);
        if (isMounted) setDailyCalories(daily);
      } catch (error) {
        console.warn('Daily calorie computation failed:', error);
      }
    }

    computeCalories();

    return () => { isMounted = false; };
  }, [user?.uid, todayActivity]);

  // Reentry state — drives the welcome-back banner and absence-aware mission
  // filtering. Cheap to compute (just looks at most-recent dates), but only
  // recomputes when the user changes.
  useEffect(() => {
    if (!user?.uid) {
      setReentryState(null);
      return;
    }
    let isMounted = true;
    (async () => {
      try {
        const [sessions, recovery, nutrition] = await Promise.all([
          getSessionHistory(),
          (async () => {
            try {
              const { getRecoveryHistory } = await import('@/lib/recoveryCalculator');
              return await getRecoveryHistory();
            } catch { return []; }
          })(),
          (async () => {
            try {
              const { db } = await import('@/lib/db');
              return await db.nutritionEntries
                .where('userId').equals(user.uid)
                .reverse()
                .limit(60)
                .toArray();
            } catch { return []; }
          })(),
        ]);
        if (!isMounted) return;
        const state = reentryStateFromHistory(sessions as any, recovery as any, nutrition as any);
        setReentryState(state);
      } catch (error) {
        console.warn('Reentry state compute failed:', error);
      }
    })();
    return () => { isMounted = false; };
  }, [user?.uid]);

  // Midnight hard-stop: clear NEAT data when the local day changes
  useEffect(() => {
    if (!user?.uid) return;
    const userId = user.uid;

    let neatDateRef = new Date().toDateString();

    function scheduleMidnightReset() {
      const now = new Date();
      const nextMidnight = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1, 0, 0, 0, 0);
      const msUntilMidnight = nextMidnight.getTime() - now.getTime();

      return setTimeout(async () => {
        neatDateRef = new Date().toDateString();
        setTodayActivity(null);
        setActivityTrend(null);
        setNeatSuppression(null);
        try {
          const { syncHealthData } = await import('@/lib/healthData');
          await syncHealthData(userId);
          const { getTodayActivity } = await import('@/lib/db');
          const today = await getTodayActivity(userId);
          if (today) setTodayActivity(today as unknown as DailyActivity);
        } catch { /* non-critical */ }
        midnightTimer = scheduleMidnightReset();
      }, msUntilMidnight);
    }

    let midnightTimer = scheduleMidnightReset();

    function handleVisibilityChange() {
      if (document.visibilityState !== 'visible') return;
      const currentDate = new Date().toDateString();
      if (currentDate !== neatDateRef) {
        neatDateRef = currentDate;
        setTodayActivity(null);
        setActivityTrend(null);
        setNeatSuppression(null);
        (async () => {
          try {
            const { syncHealthData } = await import('@/lib/healthData');
            await syncHealthData(userId);
            const { getTodayActivity } = await import('@/lib/db');
            const today = await getTodayActivity(userId);
            if (today) setTodayActivity(today as unknown as DailyActivity);
          } catch { /* non-critical */ }
        })();
        clearTimeout(midnightTimer);
        midnightTimer = scheduleMidnightReset();
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearTimeout(midnightTimer);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [user?.uid]);

  // === COMPUTED VALUES ===

  const operatorProgramMetadata: Partial<ProgramMetadata> = {
    duration: 13,
    periodizationModel: OPERATOR_PERIODIZATION
  };

  const currentPhase = getPeriodizationPhase(
    currentWeek,
    13,
    undefined,
    operatorProgramMetadata as ProgramMetadata
  );

  const loading = checkingOnboarding || !!(user && loadingProgramData);

  // === ACTION FUNCTIONS ===

  const programSupportsRangeDay = (programId?: string | null): boolean => {
    const id = (programId ?? '').toLowerCase();
    return (
      id === 'operator-strength-13week' ||
      id === 'recruit-12week' ||
      id === 'grunt-12week' ||
      id === 'specialist-12week'
    );
  };

  const shouldShowAdvancedMetrics = (): boolean => {
    if (simpleMode) return false;
    if (currentWeek === 1) return false;
    return true;
  };

  const shouldShowAIBrief = (): boolean => {
    if (simpleMode) return false;
    if (currentWeek <= 1) return false;
    return true;
  };

  const handleApplyRecommendations = () => {
    if (!smartRecs || !smartRecs.hasRecommendations) return;
    setShowToast(true);
    setTimeout(() => setShowToast(false), 3000);
  };

  const applyDeloadToToday = () => {
    if (typeof window !== 'undefined') {
      const deloadAdjustment = {
        userId: user?.uid || '',
        volumeMultiplier: 0.5,
        intensityMultiplier: 0.85,
        reason: 'ACWR Deload',
        appliedAt: new Date().toISOString(),
      };
      storage.set('deloadAdjustment', JSON.stringify(deloadAdjustment));
      setShowToast(true);
      setTimeout(() => setShowToast(false), 3000);
    }
    setDismissedDeload(true);
  };

  const startTodayWorkout = (skipRecoveryCheck: boolean = false, overrideDeload: boolean = false) => {
    if (!isPro && !isLoadingProAccess) {
      setShowProgramPaywall(true);
      return;
    }

    if (!todayRecovery && !skipRecoveryCheck) {
      setShowRecoverySkipConfirm(true);
      return;
    }

    if (currentDay === 6 && programSupportsRangeDay(programSelection?.programId)) {
      router.push(`/range?week=${currentWeek}`);
      return;
    }

    router.push(overrideDeload ? '/session?overrideDeload=true' : '/session');
  };

  const startDeloadedWorkout = () => {
    applyDeloadToToday();
    startTodayWorkout();
  };

  const handleRefreshGfit = async () => {
    if (!user?.uid) return;
    try {
      const { refreshGoogleFitToken, syncGoogleFitData } = await import('@/lib/googleFitClient');
      const refreshed = await refreshGoogleFitToken();
      if (refreshed) {
        setGfitTokenExpired(false);
        const result = await syncGoogleFitData(user.uid);
        if (result) {
          setTodayActivity(result);
        }
      }
    } catch {
      // Refresh failed silently
    }
  };

  const handleEndBreak = async () => {
    if (!user?.uid) return;
    try {
      const { endBreak } = await import('@/lib/trainingBreak');
      await endBreak(user.uid);
      setActiveBreak(null);
      const { getUserProgramState } = await import('@/lib/dayTracking');
      const state = await getUserProgramState(user.uid);
      if (state) {
        setProgramSelection(state.selection);
        setCurrentWeek(state.currentWeek);
        setCurrentDay(state.currentDay);
      }
    } catch (e) {
      console.error('Failed to end break:', e);
      alert('Failed to end break. Please try again.');
    }
  };

  const handleDayClick = async (day: number) => {
    if (day === currentDay || updatingDay) return;
    setUpdatingDay(true);
    try {
      if (user?.uid) {
        const { setCurrentDay: persistCurrentDay, getUserProgramState } = await import('@/lib/dayTracking');
        await persistCurrentDay(user.uid, currentWeek, day);
        const state = await getUserProgramState(user.uid);
        if (state) {
          setProgramSelection(state.selection);
          setCurrentWeek(state.currentWeek);
          setCurrentDay(state.currentDay);
        }
      }
    } catch (error) {
      console.error('Error updating current day from WeekTracker:', error);
    } finally {
      setUpdatingDay(false);
    }
  };

  const handleRefreshProgramState = async () => {
    if (user?.uid) {
      const { getUserProgramState } = await import('@/lib/dayTracking');
      const state = await getUserProgramState(user.uid);
      if (state) {
        setProgramSelection(state.selection);
        setCurrentWeek(state.currentWeek);
        setCurrentDay(state.currentDay);
      }
    }
  };

  return {
    // Auth & context
    user,
    router,
    currentWeek,
    currentDay,
    setCurrentWeek,
    setCurrentDay,
    oneRMs,
    isPro,
    isBeta,
    isLoadingProAccess,
    trialInfo,
    hasPreviewAccess,
    previewWorkoutsRemaining,
    lapsedTrialInfo,

    // State
    todayRecovery,
    smartRecs,
    showToast,
    setShowToast,
    checkingOnboarding,
    showDayPicker,
    setShowDayPicker,
    programSelection,
    setProgramSelection,
    showProgramPrompt,
    setShowProgramPrompt,
    loadingProgramData,
    needsDeload,
    deloadReason,
    dismissedDeload,
    setDismissedDeload,
    recoveryDebt,
    updatingDay,
    setUpdatingDay,
    showProgramPaywall,
    setShowProgramPaywall,
    volumeMetrics,
    intelligenceReport,
    workoutContext,
    simpleMode,
    archetypeNudge,
    activeOp,
    reentryState,
    activeSession,
    todayActivity,
    setTodayActivity,
    activityTrend,
    neatSuppression,
    dailyCalories,
    gfitTokenExpired,
    setGfitTokenExpired,
    workoutTab,
    setWorkoutTab,
    userPath,
    freestyleTemplates,
    recentFreestyleSessions,
    activeBreak,
    setActiveBreak,
    showRecoverySkipConfirm,
    setShowRecoverySkipConfirm,
    pendingSurvey,
    surveyMisses,

    // Computed
    currentPhase,
    loading,

    // Actions
    handleApplyRecommendations,
    applyDeloadToToday,
    startDeloadedWorkout,
    startTodayWorkout,
    shouldShowAdvancedMetrics,
    shouldShowAIBrief,
    programSupportsRangeDay,
    handleRefreshGfit,
    handleEndBreak,
    handleDayClick,
    handleRefreshProgramState,
  };
}
